/**
 * CrewPresetsEditor (053) — the crew rates on an HOURLY tariff.
 *
 * Opened from the tariff row in Settings, beside Access fees, and styled like
 * it. Each preset is trucks, movers and a rate per hour ("1 truck · 3 movers ·
 * $189"); on an hourly estimate they are the chips that fill the crew and the
 * rate in one click. The rep can still change any of the three afterwards —
 * a preset is a starting point, never a lock.
 *
 * Saved as one list on the tariff (tariffs.crew_presets), in the order shown.
 */

import React, { useState } from 'react';
import { CRM } from '../lib/theme';
import { numberInput } from '../lib/numberInput';
import { useApiClient } from '../hooks/useApi';
import { Icons } from '../lib/icons';

const T = { ...CRM };

const inputStyle: React.CSSProperties = {
  background: 'var(--crm-bg)', border: `1px solid ${T.border}`, color: T.text,
  borderRadius: '6px', padding: '5px 8px', fontSize: '12px', width: '100%', outline: 'none',
};

type Row = { trucks: string; movers: string; rate: string };

const toRows = (v: unknown): Row[] =>
  (Array.isArray(v) ? v : []).map((p: any) => ({ trucks: String(p?.trucks ?? ''), movers: String(p?.movers ?? ''), rate: String(p?.rate ?? '') }));

const complete = (r: Row) => Number(r.trucks) >= 1 && Number(r.movers) >= 1 && r.rate !== '' && Number(r.rate) >= 0;

export function CrewPresetsEditor({ tariff, onClose, onSaved }: { tariff: any; onClose: () => void; onSaved: () => void }) {
  const api = useApiClient();
  const [rows, setRows] = useState<Row[]>(() => toRows(tariff.crew_presets));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (i: number, k: keyof Row, v: string) => {
    setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    setDirty(true);
  };
  const add = () => { setRows((p) => [...p, { trucks: '1', movers: '2', rate: '' }]); setDirty(true); };
  const remove = (i: number) => { setRows((p) => p.filter((_, j) => j !== i)); setDirty(true); };

  // A half-filled row would be dropped by the server; say so instead of saving.
  const incomplete = rows.some((r) => !complete(r));

  const save = async () => {
    setBusy(true);
    try {
      await api.updateTariff(tariff.id, {
        crew_presets: rows.map((r) => ({ trucks: Number(r.trucks), movers: Number(r.movers), rate: Number(r.rate) })),
      });
      setDirty(false);
      onSaved();
    } catch (e: any) {
      window.alert(e?.message || 'Could not save crew presets');
    } finally {
      setBusy(false);
    }
  };

  const canSave = dirty && !incomplete && !busy;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 10, width: 'min(620px, 100%)', maxHeight: '86vh', overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Crew presets — {tariff.name}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer' }}><Icons.close size={18} aria-hidden="true" /></button>
        </div>
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>
          The crews a rep picks from on an hourly estimate. Picking one fills trucks, movers and the rate per hour;
          each stays editable on the estimate.
        </div>

        {rows.length === 0 && <div style={{ fontSize: 12, color: T.dim, padding: '10px 0' }}>No presets yet.</div>}
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input {...numberInput(1)} min={1} style={{ ...inputStyle, width: '62px' }} value={r.trucks} onChange={(e) => set(i, 'trucks', e.target.value)} />
              <span style={{ fontSize: 11, color: T.muted }}>{Number(r.trucks) === 1 ? 'truck' : 'trucks'}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input {...numberInput(1)} min={1} style={{ ...inputStyle, width: '62px' }} value={r.movers} onChange={(e) => set(i, 'movers', e.target.value)} />
              <span style={{ fontSize: 11, color: T.muted }}>{Number(r.movers) === 1 ? 'mover' : 'movers'}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: T.muted }}>$</span>
              <input {...numberInput(1)} min={0} style={{ ...inputStyle, width: '86px' }} value={r.rate} onChange={(e) => set(i, 'rate', e.target.value)} />
              <span style={{ fontSize: 11, color: T.muted }}>/hr</span>
            </label>
            <button onClick={() => remove(i)} title="Remove preset"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: T.red, cursor: 'pointer', padding: 4 }}>
              <Icons.delete size={15} aria-label="Remove preset" />
            </button>
          </div>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <button onClick={add}
                  style={{ background: 'none', border: `1px solid ${T.border}`, color: T.muted, borderRadius: 6, padding: '5px 11px', fontSize: 11, cursor: 'pointer' }}>
            + Add preset
          </button>
          {incomplete && <span style={{ fontSize: 11, color: T.red }}>Every preset needs trucks, movers and a rate.</span>}
          <button
            onClick={save} disabled={!canSave}
            style={{ marginLeft: 'auto', background: canSave ? T.accentFill : 'transparent', color: canSave ? 'var(--crm-on-fill)' : T.dim,
                     border: `1px solid ${canSave ? T.accentLine : T.border}`, borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                     cursor: canSave ? 'pointer' : 'default' }}
          >
            {busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>
    </div>
  );
}
